
<div class="space-y-6">
  <div>
    <h1 class="text-2xl font-bold text-gray-800">{{ 'backOffice.billing.title' | translate }}</h1>
    <p class="text-gray-600">{{ 'backOffice.billing.description' | translate }}</p>
  </div>

  @if (isLoading()) {
    <div class="text-center py-16 text-gray-500">
      <i class="fa-solid fa-spinner animate-spin text-4xl"></i>
    </div>
  } @else if (error()) {
    <div class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
      <p class="font-bold">Error</p>
      <p>{{ error() }}</p>
    </div>
  } @else {
    
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Main Billing Info (Left 2/3) -->
        <div class="lg:col-span-2 space-y-6">
             <!-- Usage & Estimation Card -->
            @if (billingSummary(); as bill) {
            <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <!-- Header Section -->
                <div class="p-6 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h2 class="text-lg font-bold text-slate-800">{{ 'backOffice.billing.summary.title' | translate }}</h2>
                        <div class="flex items-center gap-2 mt-1">
                            <p class="text-sm text-slate-500">{{ 'backOffice.billing.currentPlan' | translate }}: <span class="font-bold text-emerald-600">{{ subscription()?.plan?.name }}</span></p>
                            @if(bill.interval === 'year') {
                                <span class="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-200">YEARLY</span>
                            }
                        </div>
                    </div>
                    <div class="text-right">
                        <!-- Updated Label and Color for Pre-paid Logic -->
                        <p class="text-sm text-emerald-600 font-bold">ค่าบริการส่วนเกิน (ชำระแล้วผ่าน Wallet)</p>
                        <p class="text-3xl font-black text-emerald-600">฿{{ bill.overageFee | number:'1.2-2' }}</p>
                    </div>
                </div>
                
                <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <!-- Usage Bar -->
                    <div>
                        <div class="flex justify-between items-end mb-2">
                            <span class="text-sm font-semibold text-slate-700">{{ 'backOffice.billing.summary.casesUsed' | translate }}</span>
                            <span class="text-sm text-slate-600">
                                <span class="font-bold text-slate-900">{{ bill.used }}</span> / {{ bill.limit === Infinity ? 'Unlimited' : bill.limit }}
                            </span>
                        </div>
                        <div class="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                            <!-- Change bar color logic: Orange if over limit, but not 'Red' because it's paid -->
                            <div [class]="'h-full rounded-full transition-all ' + (bill.overageCount > 0 ? 'bg-orange-500' : 'bg-emerald-500')" 
                                [style.width.%]="bill.progressPercent"></div>
                        </div>
                        @if (bill.overageCount > 0) {
                            <p class="text-xs text-orange-600 mt-2 font-bold flex items-center">
                                <i class="fa-solid fa-circle-check mr-1"></i> 
                                ใช้เกินโควตา {{ bill.overageCount }} เคส (หักเงินจาก Wallet อัตโนมัติ)
                            </p>
                        } @else {
                            <p class="text-xs text-slate-500 mt-2">
                                {{ 'backOffice.billing.summary.remaining' | translate:{count: (bill.limit - bill.used)} }}
                            </p>
                        }
                    </div>

                    <!-- Cost Breakdown -->
                    <div class="bg-slate-50 rounded-lg p-4 text-sm space-y-2 border border-slate-100">
                        <div class="flex justify-between">
                            <span class="text-slate-600">{{ 'backOffice.billing.summary.basePlan' | translate }}</span>
                            <span class="font-semibold">฿{{ bill.basePrice | number:'1.0-0' }} / {{ bill.interval }}</span>
                        </div>
                        
                        <!-- Show Overage as a positive value then deducted -->
                        @if(bill.overageFee > 0) {
                             <div class="flex justify-between text-slate-600">
                                <span>ค่าบริการส่วนเกิน (Overage Fees)</span>
                                <span>+ ฿{{ bill.overageFee | number:'1.2-2' }}</span>
                            </div>
                            <div class="flex justify-between text-emerald-600 font-bold border-b border-dashed border-emerald-200 pb-2">
                                <span><i class="fa-solid fa-check mr-1"></i> หักจาก Wallet แล้ว (Paid)</span>
                                <span>- ฿{{ bill.overageFee | number:'1.2-2' }}</span>
                            </div>
                        }

                        <div class="pt-2 flex justify-between font-bold text-base text-slate-700">
                            <span>ยอดค้างชำระ (Total Due)</span>
                            <span class="text-emerald-600">฿0.00</span>
                        </div>
                    </div>
                </div>
            </div>
            }

            <!-- Plan Options -->
            <div>
                <div class="flex flex-col sm:flex-row justify-between items-end sm:items-center mb-6 gap-4">
                     <h3 class="text-xl font-bold text-gray-800">แพ็กเกจที่เลือกได้ (Available Plans)</h3>
                     <!-- BILLING CYCLE TOGGLE REMOVED PER REQUEST -->
                </div>

                @if(displayPlans().length === 0) {
                    <div class="bg-slate-50 border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center">
                        <i class="fa-solid fa-box-open text-5xl text-slate-300 mb-4"></i>
                        <h4 class="text-lg font-bold text-slate-600">ไม่พบแพ็กเกจที่เหมาะสม</h4>
                        <p class="text-slate-400 text-sm mt-2">กรุณาติดต่อ Admin เพื่อขอรับรายละเอียดแพ็กเกจสำหรับ {{ businessType() }}</p>
                    </div>
                } @else {
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    @for (plan of displayPlans(); track plan.id) {
                        <div class="bg-white p-6 rounded-xl shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md border border-slate-200" 
                            [class.ring-4]="subscription()?.plan_id === plan.id"
                            [class.ring-emerald-50]="subscription()?.plan_id === plan.id"
                            [class.border-emerald-500]="subscription()?.plan_id === plan.id">
                            
                            <h3 class="text-xl font-bold text-gray-800">{{ plan.name }}</h3>
                            <p class="text-3xl font-bold my-4 flex items-baseline gap-1">
                                <span>฿{{ plan.price_monthly | number }}</span>
                                <span class="text-sm font-normal text-gray-500">/ {{ plan.interval === 'year' ? 'ปี' : 'เดือน' }}</span>
                            </p>
                            
                            @if(plan.interval === 'year') {
                                <div class="text-xs text-emerald-600 font-bold bg-emerald-50 px-3 py-2 rounded-lg mb-4 border border-emerald-100">
                                    จ่ายเฉลี่ยเพียง ฿{{ (plan.price_monthly / 12) | number:'1.0-0' }} / เดือน
                                </div>
                            }
                            
                            <div class="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-100">
                                <div class="flex items-center gap-2 mb-2">
                                    <i class="fa-solid fa-user-doctor text-emerald-600 text-lg"></i>
                                    <span class="text-lg font-bold text-slate-800">
                                        {{ plan.limits['medical_cases'] ? (plan.limits['medical_cases'] | number) : 'ไม่จำกัด' }} เคส
                                    </span>
                                </div>
                                <div class="text-xs text-slate-600 flex justify-between border-t border-slate-200 pt-2">
                                    <span>เฉลี่ย {{ (plan.price_monthly / (plan.limits['medical_cases'] || 1) / (plan.interval === 'year' ? 12 : 1)) | number:'1.1-1' }} บ./เคส</span>
                                    @if(plan.limits['overage_cost']) {
                                        <span class="font-semibold text-red-500">ส่วนเกิน {{ plan.limits['overage_cost'] }} บ.</span>
                                    }
                                </div>
                            </div>

                            <ul class="space-y-3 text-sm flex-grow mb-6">
                                <li class="flex items-center gap-3">
                                    <i class="fa-solid fa-users text-slate-400 w-5 text-center"></i>
                                    <span class="text-slate-600">{{ plan.limits['users'] ? plan.limits['users'] + ' ผู้ใช้งาน' : 'ไม่จำกัดผู้ใช้งาน' }}</span>
                                </li>
                                
                                @for(feature of ['reporting', 'ai_order', 'telemedicine', 'partner_network', 'compliance_reports']; track feature) {
                                    <li class="flex items-center gap-3">
                                        <i class="fa-solid w-5 text-center" [class]="plan.features[feature] ? 'fa-check text-emerald-500' : 'fa-times text-gray-300'"></i>
                                        <span class="capitalize" [class]="plan.features[feature] ? '' : 'text-gray-400 line-through'">
                                            {{ 'backOffice.billing.features.' + feature.replace('_', '') | translate }}
                                        </span>
                                    </li>
                                }
                            </ul>

                            <div class="mt-auto">
                                <!-- Logic 1: Plan ID matches AND Status is OK (Active/Trialing) -> Disabled -->
                                @if (subscription()?.plan_id === plan.id && (subscription()?.status === 'active' || subscription()?.status === 'trialing')) {
                                    <button disabled class="w-full bg-slate-100 text-slate-500 px-5 py-3 rounded-lg font-bold cursor-default flex items-center justify-center gap-2">
                                        <i class="fa-solid fa-check-circle"></i> {{ 'backOffice.billing.currentPlanButton' | translate }}
                                    </button>
                                } 
                                <!-- Logic 2: Plan ID matches BUT Status is Bad (Past Due/Canceled) -> Enable Renew -->
                                @else if (subscription()?.plan_id === plan.id) {
                                     <button (click)="initiateUpgrade(plan)" class="w-full bg-amber-600 text-white px-5 py-3 rounded-lg hover:bg-amber-700 font-bold shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2">
                                        <i class="fa-solid fa-rotate-right"></i> ต่ออายุ (Renew Plan)
                                    </button>
                                }
                                <!-- Logic 3: Different Plan -> Upgrade/Switch -->
                                @else {
                                    <button (click)="initiateUpgrade(plan)" class="w-full bg-emerald-600 text-white px-5 py-3 rounded-lg hover:bg-emerald-700 font-bold shadow-lg transition-all transform hover:-translate-y-1">
                                        {{ 'backOffice.billing.upgradeButton' | translate }} {{ plan.name }}
                                    </button>
                                }
                            </div>
                        </div>
                    }
                    </div>
                }
            </div>
        </div>

        <!-- Wallet Section (Right 1/3) -->
        <div class="lg:col-span-1">
            <div class="bg-gradient-to-br from-indigo-900 to-slate-800 text-white rounded-xl shadow-lg p-6 relative overflow-hidden">
                <div class="absolute top-0 right-0 p-4 opacity-10">
                    <i class="fa-solid fa-wallet text-9xl"></i>
                </div>
                
                <h3 class="text-indigo-200 font-bold uppercase text-xs tracking-wider mb-2">PREPAID WALLET</h3>
                <div class="flex items-baseline gap-2 mb-6 relative z-10">
                    <span class="text-4xl font-black">{{ walletBalance().balance | number:'1.0-0' }}</span>
                    <span class="text-sm font-medium opacity-80">Credits</span>
                </div>
                
                <div class="grid grid-cols-2 gap-3 relative z-10">
                    <button (click)="initiateTopUp(500)" class="bg-white/10 hover:bg-white/20 border border-white/20 py-2 rounded-lg text-sm font-bold transition-colors">+500 (฿500)</button>
                    <button (click)="initiateTopUp(1000)" class="bg-white/10 hover:bg-white/20 border border-white/20 py-2 rounded-lg text-sm font-bold transition-colors">+1,000 (฿1,000)</button>
                    <button (click)="initiateTopUp(2000)" class="bg-white/10 hover:bg-white/20 border border-white/20 py-2 rounded-lg text-sm font-bold transition-colors">+2,000 (฿2,000)</button>
                    <button (click)="initiateTopUp(5000)" class="bg-indigo-500 hover:bg-indigo-400 border border-indigo-400 py-2 rounded-lg text-sm font-bold transition-colors shadow-lg">+5,000 (แถม 5%)</button>
                </div>
            </div>

            <!-- History -->
            <div class="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <h3 class="font-bold text-gray-800 mb-3 flex items-center gap-2"><i class="fa-solid fa-history"></i> ประวัติการใช้เครดิต</h3>
                <div class="space-y-3 max-h-[300px] overflow-y-auto">
                    @for(tx of walletHistory(); track tx.id) {
                        <div class="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                            <div class="min-w-0">
                                <p class="font-medium text-slate-700 truncate" [title]="tx.description">{{ tx.description }}</p>
                                <p class="text-[10px] text-slate-400">{{ tx.created_at | date:'d MMM y, HH:mm' }}</p>
                            </div>
                            <span class="font-bold shrink-0 ml-2" [class]="tx.amount >= 0 ? 'text-emerald-600' : 'text-red-500'">{{ tx.amount > 0 ? '+' : '' }}{{ tx.amount | number }}</span>
                        </div>
                    } @empty {
                        <p class="text-center text-slate-400 text-sm py-4">ยังไม่มีรายการ</p>
                    }
                </div>
            </div>
        </div>
    </div>
  }
</div>

<app-payment-gateway-modal
  [isOpen]="isPaymentModalOpen()"
  [plan]="selectedPlan()"
  (closeModal)="isPaymentModalOpen.set(false)"
  (paymentComplete)="onPaymentComplete()">
</app-payment-gateway-modal>
